/**
 * Lightweight gamification helpers for the WC Round Call play page.
 * - useReputation(matchday): persistent total REP + helpers to grant points
 *   and surface floating "+N REP" toasts anchored to the triggering element.
 * - RepCounter: header badge with a pulse-on-change animation.
 * - FloatingReps: portal-free overlay of currently-floating REP toasts.
 *
 * The point of this layer is to make every click *feel* rewarding — the
 * actual challenge scoring (FAP) still flows through the backend's
 * resolve_matchday_call. REP here is a session-level vanity metric that
 * encourages users to fill out the whole 20.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trophy, Star, Lightning } from "@phosphor-icons/react";

const REP_KEY = (md) => `wc_md_rep_md${md}`;
const REP_AWARDED_KEY = (md) => `wc_md_rep_awarded_md${md}`;

export const REP_RULES = {
    DECISION_BASE: 5,
    MODEL_MATCH_BONUS: 3,
    HALF_DONE_BONUS: 25,
    FULL_DONE_BONUS: 100,
    FIRST_PICK_BONUS: 10,
};

export function useReputation(matchday, players) {
    const [rep, setRep] = useState(() => {
        try { return parseInt(localStorage.getItem(REP_KEY(matchday)) || "0", 10) || 0; }
        catch { return 0; }
    });
    // Track which players already granted REP so revisiting doesn't compound.
    const awardedRef = useRef(new Set());
    useEffect(() => {
        try {
            const raw = localStorage.getItem(REP_AWARDED_KEY(matchday));
            awardedRef.current = new Set(raw ? JSON.parse(raw) : []);
        } catch { awardedRef.current = new Set(); }
    }, [matchday]);

    // Floating toasts
    const [floats, setFloats] = useState([]);
    const nextIdRef = useRef(1);

    const persist = useCallback((newRep, awardedSet) => {
        try {
            localStorage.setItem(REP_KEY(matchday), String(newRep));
            localStorage.setItem(REP_AWARDED_KEY(matchday), JSON.stringify([...awardedSet]));
        } catch { /* quota */ }
    }, [matchday]);

    const granted = useCallback((playerId) => awardedRef.current.has(String(playerId)), []);

    const pushFloat = useCallback((opts) => {
        const id = nextIdRef.current++;
        setFloats((cur) => [...cur, { id, ...opts }]);
        setTimeout(() => setFloats((cur) => cur.filter((f) => f.id !== id)), 1500);
    }, []);

    const grant = useCallback((opts) => {
        // opts: { playerId, decision, modelMatch, anchorRect, totalPlayers, decidedCount }
        const pid = String(opts.playerId);
        if (awardedRef.current.has(pid)) return; // already gave REP for this player
        let delta = REP_RULES.DECISION_BASE;
        const labels = [`+${REP_RULES.DECISION_BASE} pick`];
        if (opts.modelMatch) {
            delta += REP_RULES.MODEL_MATCH_BONUS;
            labels.push(`+${REP_RULES.MODEL_MATCH_BONUS} model`);
        }
        // First-pick bonus
        if (awardedRef.current.size === 0) {
            delta += REP_RULES.FIRST_PICK_BONUS;
            labels.push(`+${REP_RULES.FIRST_PICK_BONUS} first pick!`);
        }
        // Half/full milestones
        const newDecided = opts.decidedCount + 1; // includes this one
        const total = opts.totalPlayers;
        if (total >= 4 && newDecided === Math.ceil(total / 2)) {
            delta += REP_RULES.HALF_DONE_BONUS;
            labels.push(`+${REP_RULES.HALF_DONE_BONUS} halfway! 🔥`);
        }
        if (total >= 4 && newDecided === total) {
            delta += REP_RULES.FULL_DONE_BONUS;
            labels.push(`+${REP_RULES.FULL_DONE_BONUS} ALL IN! 🏆`);
        }
        awardedRef.current.add(pid);
        const newRep = rep + delta;
        setRep(newRep);
        persist(newRep, awardedRef.current);
        pushFloat({
            value: delta, labels, anchorRect: opts.anchorRect, decision: opts.decision,
        });
    }, [rep, persist, pushFloat]);

    const revoke = useCallback((playerId) => {
        // When user clears a decision, we don't refund REP — keep things simple
        // and one-way, like collecting stickers. But we DO unblock so a later
        // re-pick on a different decision *value* can re-grant model bonus.
        awardedRef.current.delete(String(playerId));
        persist(rep, awardedRef.current);
    }, [rep, persist]);

    return { rep, grant, revoke, floats, granted };
}

// -- Visual bits --------------------------------------------------------------

const DECISION_TINT = {
    BUY: "text-fa-mint",
    KEEP: "text-fa-yellow",
    SELL: "text-fa-amber",
    AVOID: "text-red-400",
};

export function RepCounter({ value, compact = false }) {
    const [bump, setBump] = useState(false);
    const prev = useRef(value);
    useEffect(() => {
        if (value > prev.current) {
            setBump(true);
            const t = setTimeout(() => setBump(false), 600);
            prev.current = value;
            return () => clearTimeout(t);
        }
        prev.current = value;
    }, [value]);
    return (
        <div
            className={`flex items-center gap-1.5 border border-fa-yellow/40 bg-[#1a1308] px-2 py-1 transition-transform ${
                bump ? "scale-110" : "scale-100"
            }`}
            data-testid="md-rep-counter"
            title="Reputation points earned for this round"
        >
            <Star size={compact ? 10 : 12} weight="fill" className={`text-fa-yellow ${bump ? "animate-spin" : ""}`} />
            <span className="font-display font-black text-fa-yellow tabular-nums leading-none" style={{ fontSize: compact ? 11 : 13 }}>
                {value}
            </span>
            <span className="font-mono text-[8px] uppercase tracking-widest text-fa-yellow/60">REP</span>
        </div>
    );
}

export function FloatingReps({ floats }) {
    return (
        <div className="pointer-events-none fixed inset-0 z-[60]" data-testid="md-rep-floats">
            {floats.map((f) => {
                const r = f.anchorRect || { left: window.innerWidth / 2, top: 80, width: 0 };
                const x = (r.left || 0) + (r.width || 0) / 2;
                const y = (r.top || 0);
                return (
                    <div
                        key={f.id}
                        className="absolute -translate-x-1/2 select-none rep-pop"
                        style={{ left: x, top: y }}
                    >
                        <div className={`font-display font-black ${DECISION_TINT[f.decision] || "text-fa-mint"} text-[18px] drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] flex items-center gap-1`}>
                            <Lightning size={14} weight="fill" />
                            +{f.value}
                            <span className="text-[10px] uppercase tracking-widest opacity-80">REP</span>
                        </div>
                        {f.labels && f.labels.length > 1 && (
                            <div className="font-mono text-[8px] uppercase tracking-widest text-white/70 mt-0.5 whitespace-nowrap">
                                {f.labels.slice(1).join(" · ")}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export function MilestoneBanner({ rep, decidedCount, totalPlayers }) {
    if (!totalPlayers) return null;
    const pct = Math.round(100 * decidedCount / totalPlayers);
    const allDone = decidedCount === totalPlayers;
    if (decidedCount === 0) return null;
    return (
        <div
            className={`flex items-center gap-2 px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${
                allDone ? "border-fa-mint/60 text-fa-mint bg-fa-mint/5" : "border-white/15 text-white/60 bg-white/[0.02]"
            }`}
            data-testid="md-milestone-banner"
        >
            {allDone ? (
                <>
                    <Trophy size={11} weight="fill" />
                    All 20 locked · max REP unlocked · share + submit ↓
                </>
            ) : (
                <>
                    Progress: {decidedCount}/{totalPlayers} · {pct}%
                    {pct >= 50 && <span className="text-fa-yellow">· halfway</span>}
                </>
            )}
        </div>
    );
}
